import { generateId } from './helpers';

// ============================================================================
// CORE ENTITY SCHEMA (Master Data Reference System)
// ============================================================================

export interface CoreProduct {
  id: string;
  name: string;
  description: string;
  owner: string;
  features: string[]; // linked IDs only - full objects passed in handoffs
  createdDate: Date;
  lastModified: Date;
  status: 'active' | 'planning' | 'completed' | 'archived';
}

export interface CoreFeature {
  id: string;
  title: string;
  goals: string[];
  status: 'planning' | 'in-progress' | 'review' | 'completed' | 'on-hold';
  workProducts: string[]; // linked IDs
  uploads: string[]; // linked IDs
  sdlcPhase: string;
  productId: string; // parent product ID
  createdDate: Date;
  lastModified: Date;
}

export interface CoreUpload {
  id: string;
  title: string;
  tags: string[];
  fileType: string;
  associations: string[]; // entity IDs
  createdDate: Date;
  lastModified: Date;
  size: number;
  status: 'uploading' | 'completed' | 'error';
}

export interface CoreSDLCMatrix {
  phaseId: string;
  deliverables: string[];
  status: 'not-started' | 'in-progress' | 'completed';
  dependencies: string[];
  linkedFeatures: string[];
  lastModified: Date;
}

export interface TerminologyEntry {
  id: string;
  term: string;
  aliases: string[];
  color: string;
  usageCount: number;
  category: 'tag' | 'goal' | 'status' | 'phase';
  lastUsed: Date;
}

// ============================================================================
// DATA CHANGE TRACKING
// ============================================================================

export interface DataChangeEvent {
  id: string;
  entityType: 'product' | 'feature' | 'upload' | 'sdlc-matrix' | 'terminology';
  entityId: string;
  changeType: 'create' | 'update' | 'delete' | 'archive';
  fieldChanges: Record<string, { from: any; to: any }>;
  timestamp: Date;
  propagationTargets: string[]; // IDs of entities that need updates
}

export interface WatchSubscription {
  id: string;
  entityId: string;
  entityType: string;
  callback: (change: DataChangeEvent) => void;
  filter?: (change: DataChangeEvent) => boolean;
}

// ============================================================================
// VALIDATION RULES
// ============================================================================

export interface ValidationRule {
  id: string;
  name: string;
  entityType: string;
  rule: (entity: any, context: DataContext) => ValidationResult;
  severity: 'error' | 'warning' | 'info';
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
  suggestions: string[];
}

export interface DataContext {
  allProducts: CoreProduct[];
  allFeatures: CoreFeature[];
  allUploads: CoreUpload[];
  terminologyRegistry: TerminologyEntry[];
}

// ============================================================================
// APPLICATION STATE REFERENCE
// ============================================================================

interface AppStateReference {
  initiatives: any[];
  selectedProjectId: string | null;
  selectedFeature: any | null;
}

let appStateRef: AppStateReference = {
  initiatives: [],
  selectedProjectId: null,
  selectedFeature: null
};

export const setDataHubAppState = (state: AppStateReference) => {
  appStateRef = state;
};

// ============================================================================
// DATAHUB SERVICE - Central Data Management
// ============================================================================

class DataHubService {
  private subscribers: Map<string, WatchSubscription[]> = new Map();
  private changeHistory: DataChangeEvent[] = [];
  private validationRules: ValidationRule[] = [];
  private terminologyRegistry: Map<string, TerminologyEntry> = new Map();
  private syncQueue: DataChangeEvent[] = [];
  private lastSync: Date = new Date();

  constructor() {
    this.initializeTerminologyRegistry();
    this.initializeValidationRules();
    this.loadFromStorage();
    this.startSyncProcess();
  }

  // ========================================================================
  // GOLDEN RULE ENFORCEMENT - Full Object Handoffs
  // ========================================================================

  /**
   * Get full entity with all related data for component handoffs
   * Ensures components receive FULL ENTITY OBJECTS, never just IDs
   */
  getFullEntity(entityType: string, entityId: string): any {
    const context = this.getDataContext();
    
    switch (entityType) {
      case 'product':
        const product = context.allProducts.find(p => p.id === entityId);
        if (!product) return null;
        
        return {
          ...product,
          features: product.features.map(featureId => 
            this.getFullEntity('feature', featureId)
          ).filter(Boolean)
        };

      case 'feature':
        const feature = context.allFeatures.find(f => f.id === entityId);
        if (!feature) return null;
        
        return {
          ...feature,
          workProducts: feature.workProducts.map(wpId => 
            this.getWorkProductById(wpId)
          ).filter(Boolean),
          uploads: feature.uploads.map(uploadId =>
            this.getFullEntity('upload', uploadId)
          ).filter(Boolean),
          parentProduct: this.getFullEntity('product', feature.productId)
        };

      case 'upload':
        const upload = context.allUploads.find(u => u.id === entityId);
        if (!upload) return null;
        
        return {
          ...upload,
          associatedEntities: upload.associations.map(assocId => {
            // Determine entity type and get full object
            const product = context.allProducts.find(p => p.id === assocId);
            if (product) return { type: 'product', entity: this.getFullEntity('product', assocId) };
            
            const feature = context.allFeatures.find(f => f.id === assocId);
            if (feature) return { type: 'feature', entity: this.getFullEntity('feature', assocId) };
            
            return null;
          }).filter(Boolean)
        };

      default:
        return null;
    }
  }

  // ========================================================================
  // VALIDATION SYSTEM
  // ========================================================================

  validate(entity: any, schema: string): ValidationResult {
    const context = this.getDataContext();
    const applicableRules = this.validationRules.filter(rule => 
      rule.entityType === schema || rule.entityType === 'all'
    );

    let errors: string[] = [];
    let warnings: string[] = [];
    let suggestions: string[] = [];

    // Safe validation - handle undefined/null entities
    if (!entity || typeof entity !== 'object') {
      return {
        valid: false,
        errors: ['Entity is null or undefined'],
        warnings: [],
        suggestions: ['Provide a valid entity object for validation']
      };
    }

    for (const rule of applicableRules) {
      try {
        const result = rule.rule(entity, context);
        if (!result.valid) {
          if (rule.severity === 'error') {
            errors.push(...result.errors);
          } else if (rule.severity === 'warning') {
            warnings.push(...result.warnings);
          }
          suggestions.push(...result.suggestions);
        }
      } catch (error) {
        warnings.push(`Validation rule "${rule.name}" encountered an error: ${error}`);
      }
    }

    // Terminology validation - with safe property access
    const termValidation = this.validateTerminology(entity);
    errors.push(...termValidation.errors);
    warnings.push(...termValidation.warnings);
    suggestions.push(...termValidation.suggestions);

    return {
      valid: errors.length === 0,
      errors,
      warnings,
      suggestions
    };
  }

  private validateTerminology(entity: any): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    const suggestions: string[] = [];

    // Safe validation - check if entity exists and has expected properties
    if (!entity || typeof entity !== 'object') {
      return { valid: true, errors, warnings, suggestions };
    }

    // Validate tags against terminology registry
    if (Array.isArray(entity.tags)) {
      for (const tag of entity.tags) {
        if (typeof tag === 'string') {
          const normalized = this.normalizeTerminology(tag);
          const registryEntry = this.findTerminologyEntry(normalized);
          
          if (!registryEntry) {
            const similar = this.findSimilarTerms(normalized);
            if (similar.length > 0) {
              suggestions.push(`Tag "${tag}" not in registry. Did you mean: ${similar.join(', ')}?`);
            } else {
              warnings.push(`New tag "${tag}" will be added to terminology registry`);
            }
          }
        }
      }
    }

    // Validate goals against terminology registry
    if (Array.isArray(entity.goals)) {
      for (const goal of entity.goals) {
        if (typeof goal === 'string') {
          const normalized = this.normalizeTerminology(goal);
          const registryEntry = this.findTerminologyEntry(normalized);
          
          if (!registryEntry) {
            suggestions.push(`Consider standardizing goal "${goal}" in terminology registry`);
          }
        }
      }
    }

    return { valid: errors.length === 0, errors, warnings, suggestions };
  }

  // ========================================================================
  // WATCH SYSTEM - Real-time Cross-module Updates
  // ========================================================================

  watch(entityId: string, callback: (change: DataChangeEvent) => void, filter?: (change: DataChangeEvent) => boolean): string {
    const subscriptionId = generateId();
    const subscription: WatchSubscription = {
      id: subscriptionId,
      entityId,
      entityType: this.determineEntityType(entityId),
      callback,
      filter
    };

    if (!this.subscribers.has(entityId)) {
      this.subscribers.set(entityId, []);
    }
    this.subscribers.get(entityId)!.push(subscription);

    return subscriptionId;
  }

  unwatch(subscriptionId: string): void {
    for (const [entityId, subscriptions] of this.subscribers.entries()) {
      const index = subscriptions.findIndex(sub => sub.id === subscriptionId);
      if (index !== -1) {
        subscriptions.splice(index, 1);
        if (subscriptions.length === 0) {
          this.subscribers.delete(entityId);
        }
        break;
      }
    }
  }

  // ========================================================================
  // UPDATE PROPAGATION - <2s Cross-module Sync
  // ========================================================================

  async propagateChange(change: DataChangeEvent): Promise<void> {
    const startTime = Date.now();
    
    // Add to change history
    this.changeHistory.push(change);
    
    // Add to sync queue for cloud backup
    this.syncQueue.push(change);
    
    // Determine propagation targets
    const targets = await this.calculatePropagationTargets(change);
    change.propagationTargets = targets;
    
    // Notify direct subscribers
    const directSubscribers = this.subscribers.get(change.entityId) || [];
    for (const subscription of directSubscribers) {
      if (!subscription.filter || subscription.filter(change)) {
        try {
          subscription.callback(change);
        } catch (error) {
          console.error(`Error in subscription callback for ${subscription.id}:`, error);
        }
      }
    }
    
    // Propagate to related entities
    for (const targetId of targets) {
      const targetSubscribers = this.subscribers.get(targetId) || [];
      for (const subscription of targetSubscribers) {
        if (!subscription.filter || subscription.filter(change)) {
          try {
            subscription.callback(change);
          } catch (error) {
            console.error(`Error in propagation callback for ${subscription.id}:`, error);
          }
        }
      }
    }
    
    // Update terminology usage if applicable
    if (change.fieldChanges.tags || change.fieldChanges.goals) {
      this.updateTerminologyUsage(change);
    }
    
    // Persist changes
    this.saveToStorage();
    
    const endTime = Date.now();
    const propagationTime = endTime - startTime;
    
    // Ensure <2s propagation time
    if (propagationTime > 2000) {
      console.warn(`Propagation time exceeded 2s: ${propagationTime}ms for change ${change.id}`);
    }
    
    // Trigger audit event
    this.recordAuditEvent({
      type: 'data-propagation',
      entityId: change.entityId,
      propagationTime,
      targetCount: targets.length,
      timestamp: new Date()
    });
  }

  private async calculatePropagationTargets(change: DataChangeEvent): Promise<string[]> {
    const targets: Set<string> = new Set();
    const context = this.getDataContext();
    
    switch (change.entityType) {
      case 'feature':
        // Feature changes propagate to parent product and related uploads
        const feature = context.allFeatures.find(f => f.id === change.entityId);
        if (feature) {
          targets.add(feature.productId);
          feature.uploads.forEach(uploadId => targets.add(uploadId));
          feature.workProducts.forEach(wpId => targets.add(wpId));
        }
        break;
        
      case 'upload':
        // Upload changes propagate to all associated entities
        const upload = context.allUploads.find(u => u.id === change.entityId);
        if (upload) {
          upload.associations.forEach(assocId => targets.add(assocId));
        }
        break;
        
      case 'product':
        // Product changes propagate to all features
        const product = context.allProducts.find(p => p.id === change.entityId);
        if (product) {
          product.features.forEach(featureId => targets.add(featureId));
        }
        break;
    }
    
    return Array.from(targets);
  }

  // ========================================================================
  // TERMINOLOGY REGISTRY
  // ========================================================================

  private initializeTerminologyRegistry(): void {
    // Initialize with common terms
    const commonTerms = [
      { term: 'requirements', aliases: ['requirement', 'req', 'specs'], category: 'tag', color: '#103699' },
      { term: 'design', aliases: ['ui', 'ux', 'mockup', 'wireframe'], category: 'tag', color: '#4CAF50' },
      { term: 'development', aliases: ['dev', 'code', 'implementation'], category: 'tag', color: '#FF9800' },
      { term: 'testing', aliases: ['qa', 'test', 'validation'], category: 'tag', color: '#F44336' },
      { term: 'documentation', aliases: ['docs', 'doc', 'guide'], category: 'tag', color: '#9C27B0' },
      { term: 'user-experience', aliases: ['ux', 'usability'], category: 'goal', color: '#2196F3' },
      { term: 'performance', aliases: ['speed', 'optimization'], category: 'goal', color: '#4CAF50' },
      { term: 'security', aliases: ['auth', 'authorization', 'safety'], category: 'goal', color: '#F44336' },
    ];

    for (const term of commonTerms) {
      const entry: TerminologyEntry = {
        id: generateId(),
        term: term.term,
        aliases: term.aliases,
        color: term.color,
        usageCount: 0,
        category: term.category as any,
        lastUsed: new Date()
      };
      this.terminologyRegistry.set(term.term, entry);
    }
  }

  private normalizeTerminology(term: string): string {
    return term.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-');
  }

  private findTerminologyEntry(normalizedTerm: string): TerminologyEntry | null {
    for (const entry of this.terminologyRegistry.values()) {
      if (entry.term === normalizedTerm || entry.aliases.includes(normalizedTerm)) {
        return entry;
      }
    }
    return null;
  }

  private findSimilarTerms(term: string, maxResults: number = 3): string[] {
    const similarities: Array<{ term: string; score: number }> = [];
    
    for (const entry of this.terminologyRegistry.values()) {
      const score = this.calculateSimilarity(term, entry.term);
      if (score > 0.6) {
        similarities.push({ term: entry.term, score });
      }
      
      for (const alias of entry.aliases) {
        const aliasScore = this.calculateSimilarity(term, alias);
        if (aliasScore > 0.6) {
          similarities.push({ term: alias, score: aliasScore });
        }
      }
    }
    
    return similarities
      .sort((a, b) => b.score - a.score)
      .slice(0, maxResults)
      .map(s => s.term);
  }

  private calculateSimilarity(str1: string, str2: string): number {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;
    
    if (longer.length === 0) return 1.0;
    
    const editDistance = this.levenshteinDistance(longer, shorter);
    return (longer.length - editDistance) / longer.length;
  }

  private levenshteinDistance(str1: string, str2: string): number {
    const matrix = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null));
    
    for (let i = 0; i <= str1.length; i++) matrix[0][i] = i;
    for (let j = 0; j <= str2.length; j++) matrix[j][0] = j;
    
    for (let j = 1; j <= str2.length; j++) {
      for (let i = 1; i <= str1.length; i++) {
        const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
        matrix[j][i] = Math.min(
          matrix[j][i - 1] + 1,
          matrix[j - 1][i] + 1,
          matrix[j - 1][i - 1] + indicator
        );
      }
    }
    
    return matrix[str2.length][str1.length];
  }

  private updateTerminologyUsage(change: DataChangeEvent): void {
    const updateTermUsage = (terms: string[]) => {
      for (const term of terms) {
        if (typeof term === 'string') {
          const normalized = this.normalizeTerminology(term);
          let entry = this.findTerminologyEntry(normalized);
          
          if (!entry) {
            // Create new terminology entry
            entry = {
              id: generateId(),
              term: normalized,
              aliases: [],
              color: this.generateTermColor(),
              usageCount: 1,
              category: 'tag',
              lastUsed: new Date()
            };
            this.terminologyRegistry.set(normalized, entry);
          } else {
            entry.usageCount++;
            entry.lastUsed = new Date();
          }
        }
      }
    };

    if (change.fieldChanges.tags && Array.isArray(change.fieldChanges.tags.to)) {
      updateTermUsage(change.fieldChanges.tags.to);
    }

    if (change.fieldChanges.goals && Array.isArray(change.fieldChanges.goals.to)) {
      updateTermUsage(change.fieldChanges.goals.to);
    }
  }

  private generateTermColor(): string {
    const colors = ['#103699', '#4CAF50', '#FF9800', '#F44336', '#9C27B0', '#2196F3', '#795548'];
    return colors[Math.floor(Math.random() * colors.length)];
  }

  // ========================================================================
  // STORAGE ARCHITECTURE
  // ========================================================================

  private saveToStorage(): void {
    try {
      // LocalStorage Cache - Quick access
      const cacheData = {
        lastSync: this.lastSync,
        changeCount: this.changeHistory.length,
        terminologyCount: this.terminologyRegistry.size
      };
      localStorage.setItem('dataHub_cache', JSON.stringify(cacheData));
      
      // IndexedDB - Full data persistence
      this.saveToIndexedDB();
      
    } catch (error) {
      console.error('Failed to save to storage:', error);
    }
  }

  private loadFromStorage(): void {
    try {
      // Load from LocalStorage cache
      const cacheData = localStorage.getItem('dataHub_cache');
      if (cacheData) {
        const cache = JSON.parse(cacheData);
        this.lastSync = new Date(cache.lastSync);
      }
      
      // Load from IndexedDB
      this.loadFromIndexedDB();
      
    } catch (error) {
      console.error('Failed to load from storage:', error);
    }
  }

  private async saveToIndexedDB(): Promise<void> {
    // Implementation would use IndexedDB API
    // For now, simulate with enhanced localStorage
    try {
      const data = {
        changeHistory: this.changeHistory.slice(-1000), // Keep last 1000 changes
        terminologyRegistry: Array.from(this.terminologyRegistry.entries()),
        lastSync: this.lastSync
      };
      localStorage.setItem('dataHub_indexedDB', JSON.stringify(data));
    } catch (error) {
      console.error('Failed to save to IndexedDB simulation:', error);
    }
  }

  private async loadFromIndexedDB(): Promise<void> {
    try {
      const data = localStorage.getItem('dataHub_indexedDB');
      if (data) {
        const parsed = JSON.parse(data);
        this.changeHistory = parsed.changeHistory || [];
        this.terminologyRegistry = new Map(parsed.terminologyRegistry || []);
        this.lastSync = new Date(parsed.lastSync || Date.now());
      }
    } catch (error) {
      console.error('Failed to load from IndexedDB simulation:', error);
    }
  }

  private startSyncProcess(): void {
    // Auto-sync to cloud backup every 30 seconds
    setInterval(() => {
      this.syncToCloud();
    }, 30000);
  }

  private async syncToCloud(): Promise<void> {
    if (this.syncQueue.length === 0) return;
    
    try {
      // Simulate cloud sync - would integrate with actual cloud service
      console.log(`Syncing ${this.syncQueue.length} changes to cloud backup`);
      
      // Clear sync queue after successful sync
      this.syncQueue = [];
      this.lastSync = new Date();
      
      this.recordAuditEvent({
        type: 'cloud-sync',
        timestamp: new Date(),
        changeCount: this.syncQueue.length
      });
      
    } catch (error) {
      console.error('Cloud sync failed:', error);
      // Retain changes in queue for retry
    }
  }

  // ========================================================================
  // AUDIT FRAMEWORK
  // ========================================================================

  private auditEvents: any[] = [];

  private recordAuditEvent(event: any): void {
    this.auditEvents.push({
      id: generateId(),
      ...event,
      timestamp: event.timestamp || new Date()
    });
    
    // Keep only last 10000 audit events
    if (this.auditEvents.length > 10000) {
      this.auditEvents = this.auditEvents.slice(-10000);
    }
  }

  getAuditReport(): any {
    const now = new Date();
    const oneHour = 60 * 60 * 1000;
    const oneDay = 24 * oneHour;
    
    const recentEvents = this.auditEvents.filter(e => 
      now.getTime() - e.timestamp.getTime() < oneDay
    );
    
    const propagationEvents = recentEvents.filter(e => e.type === 'data-propagation');
    const avgPropagationTime = propagationEvents.length > 0 
      ? propagationEvents.reduce((sum, e) => sum + e.propagationTime, 0) / propagationEvents.length
      : 0;
    
    return {
      period: '24 hours',
      metrics: {
        totalChanges: recentEvents.filter(e => e.type === 'data-propagation').length,
        averagePropagationTime: `${avgPropagationTime.toFixed(0)}ms`,
        terminologyConsistencyScore: this.calculateTerminologyConsistency(),
        orphanDetectionRate: this.calculateOrphanDetectionRate(),
        cloudSyncEvents: recentEvents.filter(e => e.type === 'cloud-sync').length
      },
      compliance: {
        propagationTimeCompliant: avgPropagationTime < 2000,
        terminologyCompliant: this.calculateTerminologyConsistency() > 0.8,
        dataIntegrityCompliant: this.calculateOrphanDetectionRate() < 0.01
      }
    };
  }

  private calculateTerminologyConsistency(): number {
    // Calculate percentage of terms that match registry
    const context = this.getDataContext();
    let totalTerms = 0;
    let registeredTerms = 0;
    
    // Check all entities for terminology compliance
    for (const upload of context.allUploads) {
      for (const tag of upload.tags || []) {
        totalTerms++;
        if (this.findTerminologyEntry(this.normalizeTerminology(tag))) {
          registeredTerms++;
        }
      }
    }
    
    for (const feature of context.allFeatures) {
      for (const goal of feature.goals || []) {
        totalTerms++;
        if (this.findTerminologyEntry(this.normalizeTerminology(goal))) {
          registeredTerms++;
        }
      }
    }
    
    return totalTerms > 0 ? registeredTerms / totalTerms : 1;
  }

  private calculateOrphanDetectionRate(): number {
    // Calculate percentage of orphaned references
    const context = this.getDataContext();
    let totalReferences = 0;
    let orphanedReferences = 0;
    
    // Check feature -> product references
    for (const feature of context.allFeatures) {
      totalReferences++;
      if (!context.allProducts.find(p => p.id === feature.productId)) {
        orphanedReferences++;
      }
    }
    
    // Check upload associations
    for (const upload of context.allUploads) {
      for (const assocId of upload.associations) {
        totalReferences++;
        const isValidAssoc = context.allProducts.find(p => p.id === assocId) ||
                           context.allFeatures.find(f => f.id === assocId);
        if (!isValidAssoc) {
          orphanedReferences++;
        }
      }
    }
    
    return totalReferences > 0 ? orphanedReferences / totalReferences : 0;
  }

  // ========================================================================
  // HELPER METHODS
  // ========================================================================

  private getDataContext(): DataContext {
    // Return actual application state instead of empty arrays
    const allProducts: CoreProduct[] = [];
    const allFeatures: CoreFeature[] = [];
    const allUploads: CoreUpload[] = [];

    // Convert initiatives to core entities
    for (const initiative of appStateRef.initiatives) {
      // Convert initiative to product
      const product: CoreProduct = {
        id: initiative.id,
        name: initiative.name,
        description: initiative.description || '',
        owner: initiative.owner || 'Unknown',
        features: initiative.features?.map((f: any) => f.id) || [],
        createdDate: new Date(initiative.createdDate || Date.now()),
        lastModified: new Date(),
        status: initiative.status === 'active' ? 'active' : 'planning'
      };
      allProducts.push(product);

      // Convert features
      if (initiative.features) {
        for (const feature of initiative.features) {
          const coreFeature: CoreFeature = {
            id: feature.id,
            title: feature.name || feature.title || 'Untitled Feature',
            goals: feature.goals || [],
            status: 'planning',
            workProducts: [],
            uploads: feature.uploads?.map((u: any) => u.id) || [],
            sdlcPhase: 'discovery',
            productId: initiative.id,
            createdDate: new Date(),
            lastModified: new Date()
          };
          allFeatures.push(coreFeature);
        }
      }

      // Convert uploads
      if (initiative.uploads) {
        for (const upload of initiative.uploads) {
          const coreUpload: CoreUpload = {
            id: upload.id,
            title: upload.name || 'Untitled File',
            tags: upload.tags || [],
            fileType: upload.type || 'application/octet-stream',
            associations: [initiative.id],
            createdDate: new Date(),
            lastModified: new Date(),
            size: upload.size || 0,
            status: upload.status === 'completed' ? 'completed' : 'uploading'
          };
          allUploads.push(coreUpload);
        }
      }
    }

    return {
      allProducts,
      allFeatures,
      allUploads,
      terminologyRegistry: Array.from(this.terminologyRegistry.values())
    };
  }

  private determineEntityType(entityId: string): string {
    // Determine entity type based on ID pattern or lookup
    if (entityId.startsWith('prod-')) return 'product';
    if (entityId.startsWith('feat-')) return 'feature';
    if (entityId.startsWith('upload-')) return 'upload';
    if (entityId.startsWith('sdlc-')) return 'sdlc-matrix';
    if (entityId.startsWith('init-')) return 'product'; // initiatives are products
    return 'unknown';
  }

  private getWorkProductById(wpId: string): any {
    // This would integrate with actual work product storage
    return null;
  }

  private initializeValidationRules(): void {
    // Core validation rules with safe property access
    this.validationRules = [
      {
        id: 'required-fields',
        name: 'Required Fields Validation',
        entityType: 'all',
        severity: 'error',
        rule: (entity, context) => {
          const errors: string[] = [];
          if (!entity || typeof entity !== 'object') {
            errors.push('Entity must be a valid object');
            return { valid: false, errors, warnings: [], suggestions: [] };
          }
          
          if (!entity.id) errors.push('ID is required');
          if (!entity.name && !entity.title) errors.push('Name or title is required');
          
          return {
            valid: errors.length === 0,
            errors,
            warnings: [],
            suggestions: errors.length > 0 ? ['Ensure all required fields are populated'] : []
          };
        }
      },
      {
        id: 'relationship-integrity',
        name: 'Relationship Integrity Check',
        entityType: 'feature',
        severity: 'warning', // Changed to warning to be less strict
        rule: (entity, context) => {
          const errors: string[] = [];
          const warnings: string[] = [];
          
          if (!entity || typeof entity !== 'object') {
            return { valid: true, errors, warnings, suggestions: [] };
          }
          
          const feature = entity as CoreFeature;
          
          // Check if parent product exists (only warn if missing)
          if (feature.productId && !context.allProducts.find(p => p.id === feature.productId)) {
            warnings.push(`Parent product ${feature.productId} not found in current context`);
          }
          
          return {
            valid: errors.length === 0,
            errors,
            warnings,
            suggestions: []
          };
        }
      }
    ];
  }
}

// ============================================================================
// SINGLETON INSTANCE & PUBLIC API
// ============================================================================

export const DataHub = new DataHubService();

// Export public API methods that match the specification
export const DataHubAPI = {
  validate: (entity: any, schema: string) => DataHub.validate(entity, schema),
  watch: (entityId: string, callback: (change: DataChangeEvent) => void) => DataHub.watch(entityId, callback),
  unwatch: (subscriptionId: string) => DataHub.unwatch(subscriptionId),
  getFullEntity: (entityType: string, entityId: string) => DataHub.getFullEntity(entityType, entityId),
  propagateChange: (change: DataChangeEvent) => DataHub.propagateChange(change),
  getAuditReport: () => DataHub.getAuditReport()
};

// setDataHubAppState is already exported above